import { z } from 'zod';
/**
 * CAN operations — the working dashboard for a fleet that is reporting.
 *
 * Everything in this contract is MEASURED from the 41-parameter catalogue. The
 * page that consumes it also shows illustrative panels (EV state of charge,
 * maintenance scheduling, cost savings, driver safety scores), and those are
 * deliberately absent from here: the platform cannot compute them, so they live
 * in the web layer as clearly-badged sample data and never enter the database
 * or an API response. Anything reaching a client through this endpoint is a
 * real reading.
 *
 * The distinction matters more than it looks. A figure that arrives over the
 * API is indistinguishable from a measurement six months later, when whoever
 * added it has moved on.
 */
/**
 * Parameters read for the live panels.
 *
 * Chosen to answer "what is this vehicle doing right now" rather than "is it
 * healthy" — which is what `TELEMETRY_HEALTH_PARAMETERS` already covers, over a
 * different window and with different refusals.
 */
export const CAN_OPERATIONS_LIVE_PARAMETERS = [
    'engineRpm',
    'engineLoadPct',
    'engineCoolantTempC',
    'engineOilPressureKpa',
    'acceleratorPedalPct',
    'brakePedalPct',
    'cruiseControlActive',
    'gearPosition',
    'serviceBrakeActive',
    'driverSeatbeltFastened',
    'vehicleSpeedKph',
    'malfunctionLamp',
    'activeDtcCount',
];
export const CAN_OPERATIONS_DEFAULT_DAYS = 7;
export const CAN_OPERATIONS_MIN_DAYS = 1;
export const CAN_OPERATIONS_MAX_DAYS = 30;
/** Entries in the driver and vehicle efficiency rankings. */
export const CAN_OPERATIONS_RANKING_LIMIT = 10;
/**
 * Below this distance a fuel-economy figure is withheld.
 *
 * Economy is a distance divided by a volume, and both come from odometer and
 * cumulative-fuel counters that tick in coarse increments. Over a few hundred
 * metres the rounding dominates the measurement, and the resulting "112 mpg"
 * would be read as a result rather than as noise.
 */
export const CAN_OPERATIONS_MIN_KM_FOR_ECONOMY = 25;
/** Litres below which the same applies, for a vehicle that idled a long way. */
export const CAN_OPERATIONS_MIN_LITRES_FOR_ECONOMY = 5;
export const canOperationsQuerySchema = z
    .object({
    days: z.coerce
        .number()
        .int()
        .min(CAN_OPERATIONS_MIN_DAYS)
        .max(CAN_OPERATIONS_MAX_DAYS)
        .default(CAN_OPERATIONS_DEFAULT_DAYS),
    /** The vehicle whose live readings are returned. Defaults to the most recent reporter. */
    vehicleId: z.string().uuid().optional(),
})
    .strict();
/** Why an economy figure is not offered. Never rendered as zero. */
export const economyUnavailableSchema = z.enum([
    'INSUFFICIENT_DISTANCE',
    'INSUFFICIENT_FUEL',
    'NO_COUNTER_DATA',
]);
export const economyFigureSchema = z.object({
    /**
     * Kilometres per litre. Null whenever `unavailable` is set.
     *
     * The ONLY economy figure the API returns. An `mpg` field used to sit beside
     * it and was always IMPERIAL gallons — which silently contradicted any tenant
     * set to US gallons, a 20% error with nothing on screen looking wrong. The
     * unit is a display choice now, so it is made once in the browser.
     */
    kmPerL: z.number().nullable(),
    km: z.number(),
    litres: z.number(),
    unavailable: economyUnavailableSchema.nullable(),
});
export const canOperationsDaySchema = z.object({
    date: z.string(),
    kmPerL: z.number().nullable(),
    km: z.number(),
    litres: z.number(),
});
export const economyRankSchema = z.object({
    id: z.string(),
    name: z.string(),
    kmPerL: z.number(),
    km: z.number(),
    litres: z.number(),
});
/** One live reading. `value` is null when the vehicle has never sent it. */
export const liveReadingSchema = z.object({
    parameter: z.string(),
    numeric: z.number().nullable(),
    boolean: z.boolean().nullable(),
    at: z.string().datetime().nullable(),
    ageMinutes: z.number().nullable(),
});
export const canOperationsVehicleSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    lastSampleAt: z.string().datetime().nullable(),
});
/** One active fault as the provider sent it. Description is absent, never invented. */
export const liveDtcSchema = z.object({
    code: z.string(),
    description: z.string().nullable(),
    severity: z.string().nullable(),
});
export const canOperationsLiveSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    driverId: z.string().uuid().nullable(),
    driverName: z.string().nullable(),
    at: z.string().datetime().nullable(),
    readings: z.array(liveReadingSchema),
    /**
     * Codes from the most recent frame that carried any.
     *
     * Null means no frame reported codes at all — distinct from an empty array,
     * which is the ECU positively saying there are none.
     */
    dtcCodes: z.array(liveDtcSchema).nullable(),
    dtcReportedAt: z.string().datetime().nullable(),
});
/**
 * Fault posture across the fleet.
 *
 * `faultFree` counts vehicles REPORTING in the window with no active DTC and no
 * malfunction lamp. Vehicles that sent nothing are excluded rather than counted
 * as fault-free, because silence is not health — the same distinction the CAN
 * fleet page draws between stale and never-reported.
 */
/** Every vehicle currently reporting fault codes, so none has to be hunted for. */
export const fleetFaultSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    at: z.string().datetime(),
    ageMinutes: z.number(),
    codes: z.array(liveDtcSchema),
});
/** Vehicles listed in the fleet fault panel before it says it has truncated. */
export const CAN_OPERATIONS_FAULT_LIMIT = 25;
export const canOperationsFaultsSchema = z.object({
    vehiclesReporting: z.number().int(),
    vehiclesActive: z.number().int(),
    faultFree: z.number().int(),
    withActiveDtc: z.number().int(),
    withMalfunctionLamp: z.number().int(),
    activeDtcTotal: z.number().int(),
    /**
     * Every vehicle reporting codes, most recent first.
     *
     * Empty means no vehicle sent codes — which is NOT the same as every vehicle
     * being well, since a vehicle that reports nothing reports no faults either.
     * The panel says so rather than declaring the fleet healthy.
     */
    vehiclesWithCodes: z.array(fleetFaultSchema),
    faultListTruncated: z.boolean(),
});
/**
 * One electric vehicle's traction pack, as last reported.
 *
 * ⚠️ **There is no state of HEALTH here, and its absence is deliberate.** The
 * panel this replaces showed a "fleet average battery health 94%" figure, and
 * nothing on the bus reports one: state of health is a manufacturer's
 * computation over the pack's whole life, not a CAN parameter. State of charge
 * is measured; health would be invented, and putting the two side by side is
 * exactly how an invented number acquires the credibility of a measured one.
 */
export const evVehicleStateSchema = z.object({
    vehicleId: z.string().uuid(),
    name: z.string(),
    registration: z.string(),
    stateOfChargePct: z.number(),
    /** Null when the vehicle reported charge but no range estimate. */
    estimatedRangeKm: z.number().nullable(),
    charging: z.boolean(),
    /**
     * Moving at the last reading. With `charging` this is what the screen turns
     * into a word — "charging", "in use", "idle" — rather than the platform
     * storing a state string it would then have to keep in step with two booleans.
     */
    moving: z.boolean(),
    at: z.string().datetime(),
});
export const canOperationsEvSchema = z.object({
    /** Latest reading per vehicle that reported a traction pack in the window. */
    vehicles: z.array(evVehicleStateSchema),
    /**
     * Vehicles that reported at all in the window, so the panel can say what its
     * DENOMINATOR is. Three EVs out of eighty vehicles is a fact about the fleet;
     * three out of three is a different one, and the pins alone cannot tell them
     * apart — the same argument `activeVehicles` makes on the fleet map.
     */
    reportingVehicles: z.number().int(),
});
/**
 * One driver's safety score.
 *
 * ⚠️ **`score` is NULLABLE and that is load-bearing**, exactly as `kmPerL` is on
 * the economy figure beside it. Below a distance floor a rate is arithmetic
 * rather than evidence, and a reader shown 40/100 will act on it. The counts
 * stay — they are facts — and `withheld` says which absence this is.
 */
export const driverSafetyScoreSchema = z.object({
    driverId: z.string().uuid(),
    driver: z.string(),
    score: z.number().nullable(),
    band: z.enum(['GOOD', 'FAIR', 'POOR']).nullable(),
    riskPer1000Km: z.number().nullable(),
    incidentCount: z.number().int(),
    distanceKm: z.number(),
    withheld: z.literal('INSUFFICIENT_DISTANCE').nullable(),
});
export const canOperationsResponseSchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    days: z.number().int(),
    fleetEconomy: economyFigureSchema,
    daily: z.array(canOperationsDaySchema),
    byDriver: z.array(economyRankSchema),
    byVehicle: z.array(economyRankSchema),
    faults: canOperationsFaultsSchema,
    /** Selectable vehicles, most recently heard from first. */
    vehicles: z.array(canOperationsVehicleSchema),
    /**
     * True when more vehicles reported than the picker lists.
     *
     * ⚠️ Exists because the cap used to be silent: 50 on a platform whose seeded
     * tenant holds 107, so a reader searching for a vehicle past it found no match
     * and would reasonably conclude it was not reporting. Silent truncation
     * reading as "everything is here" is the failure `faultListTruncated` and
     * `PIPELINE_FAILED_REASON_LIMIT` already exist to prevent.
     */
    vehiclesTruncated: z.boolean(),
    /** Null when the tenant has no vehicle that has reported at all. */
    live: canOperationsLiveSchema.nullable(),
    /** Electric vehicles. Empty `vehicles` means none reported a traction pack. */
    ev: canOperationsEvSchema,
    /** Driver safety, worst first — the end a reader has to act on. */
    driverSafety: z.array(driverSafetyScoreSchema),
    /** The distance floor below which a score is withheld, so the page can say it. */
    minKmForSafetyScore: z.number(),
    minKmForEconomy: z.number(),
    minLitresForEconomy: z.number(),
});
//# sourceMappingURL=can-operations.js.map